import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { styled, keyframes } from "styled-components";
import ReactMarkdown from "react-markdown";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import remarkGfm from "remark-gfm";
import { dracula } from "react-syntax-highlighter/dist/esm/styles/prism";
import { DateTag, TagContainer } from "../home/Contents";
import { Tag } from "../public/Tags";
import { CommentIF, ContentIF } from "../types";
import Comments from "./Comments";
import axios from "axios";
import { ViewIcon, ViewIconContainer, HeartIconContainer, HeartIcon, TagIconContainer, TagIcon, CalenderIconContainer, CalendarIcon, ModifyIconContainer, ModifyIcon, TrashIconContainer, TrashIcon } from "../icons";
import { deleteContent, increaseHeart, increaseView } from "../api/contents";
import { isLoggedIn } from "../login/Login";
import { MdExtra } from "../writing/Writing";
import Header from "../public/Header";
import Copyright from "../public/Copyright";

const HEADER_HEIGHT = 60;
const HEADER_BOTTOM = 30;

/* Content.tsx는 게시물를 확인할 수 있는 컴포넌트 (게시물 정보: 게시 날짜, 태그, 뷰 등...) */

const WhileUnusual = styled.div`
    padding-top: 35vh;
    font-size: 3rem;
    font-weight: bold;
    color: rgba(0, 0, 0, 0.5);
    text-align: center;
`;

const modalShow = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.96);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const BG = styled.div`
  height: 100vh;                 /* 🔥 전체 화면 고정 */
  display: flex;
  flex-direction: column;
  overflow: hidden;              /* 🔥 전체 스크롤 막기 */
  background: linear-gradient(135deg, ${props => props.theme.ROOT_BACKGROUND.FIRST} 0%, ${props => props.theme.ROOT_BACKGROUND.LAST}  100%);
  font-family: 'Pretendard', -apple-system, sans-serif;
`;

const Box = styled.section`
    background-color: ${props => props.theme.BOX_BACKGROUND};
    width: 80rem;
    margin: 0 auto 2rem auto;
    padding: 1rem 2rem;

    border: 1px solid ${props => props.theme.BOX_BORDER};
    border-radius: 7px; 
    box-shadow: 0 2px 4px -1px ${props => props.theme.BOX_SHADOW.FIRST}, 0 2px 4px -1px ${props => props.theme.BOX_SHADOW.LAST};
`;

const Body = styled.div`
  width: 100%;
  margin: 0 auto;
  gap: 2rem;

  margin-top: ${HEADER_HEIGHT}px;  /* 🔥 헤더만큼 밀기 */
  overflow-y: scroll;
`;

const TitleContainer = styled(Box)`
    display: flex;
    flex-direction: row;
    margin: 1rem auto 0 auto;

    & > div {
        font-size: 1.5rem;
        font-weight: bold;
    }
`;

const Title = styled.div`
    font-size: 1.5rem;
    font-weight: bold;
    color: ${props => props.theme.CONTENT.TITLE_COLOR};
`;

const IconContainer = styled.div`
    color: ${props => props.theme.CONTENT.SUMMARY_COLOR};
`;

const PostContainer = styled(MdExtra)`
    background-color: ${props => props.theme.CONTENT.BACKGROUND};
    width: 80rem;
    padding: 1rem 2rem;
    margin: 1rem auto 0 auto;
    transition: transform 0.3s ease, box-shadow 0.3 ease;
    border: 1px solid ${props => props.theme.CONTENT.BORDER};
    border-radius: 7px; 
    box-shadow: 0 4px 6px -1px ${props => props.theme.CONTENT.SHADOW};
    color: ${props => props.theme.CONTENT.TITLE_COLOR};
    font-size: 1.1rem;
`;

const InfoContainer = styled(Box)`
    margin: 1rem auto 0 auto;
`;

const InfoSubContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-content: flex-start;
    align-items: center;
`;

const InfoSubTitle = styled.div`
    font-size: 1rem;
    font-weight: bold;
    letter-spacing: -1px;
    margin-right: 1rem;
    margin-top: 0.5rem;
    margin-bottom: auto;
    color: ${props => props.theme.CONTENT.TITLE_COLOR};
`;

const CommentContainer = styled(Box)`
    margin: 1rem auto 4rem auto;

    & > div {
        font-size: 1.5rem;
        font-weight: bold;
    }
`;

const AlertContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 200;

    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;

    z-index: 200;
`

const DelContentAlert = styled.div`
  width: 28rem;
  padding: 2.5rem 2rem;

  background: ${props => props.theme.ROOT_BACKGROUND.FIRST};
  border-radius: 16px;
  box-shadow: 0 25px 50px -12px rgba(0,0,0,0.25);

  display: flex;
  flex-direction: column;
  align-items: center;

  animation: ${modalShow} 0.25s ease-out;
`;

const AlertIcon = styled.div`
  width: 3.5rem;
  height: 3.5rem;

  display: flex;
  justify-content: center;
  align-items: center;

  border-radius: 50%;
  background: #fee2e2;
  color: #dc2626;

  font-size: 1.8rem;
  margin-bottom: 1rem;
`;

const AlertTitle = styled.div`
  font-size: 1.25rem;
  font-weight: 700;
  color: ${props => props.theme.CONTENT.TITLE_COLOR};
  margin-bottom: 0.5rem;
`;

const AlertDesc = styled.div`
  font-size: 0.95rem;
  color: ${props => props.theme.CONTENT.SUMMARY_COLOR};
  text-align: center;
`;

const BtnContainer = styled.div`
  margin-top: 2rem;
  display: flex;
  gap: 1rem;
  width: 100%;
`;

const CancelBtn = styled.button`
  flex: 1;
  height: 2.75rem;

  border-radius: 10px;
  border: 1px solid #cbd5e1;

  background: white;
  color: #334155;
  font-weight: 600;

  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f1f5f9;
  }
`;

const DeleteBtn = styled.button`
  flex: 1;
  height: 2.75rem;

  border-radius: 10px;
  border: none;

  background: #ef4444;
  color: white;
  font-weight: 600;

  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #dc2626;
    box-shadow: 0 8px 20px rgba(239, 68, 68, 0.4);
  }
`;

function Content() {
    // content path: contents/id ~ param은 무조건 string!
    const { id } = useParams<{id: string}>();
    const [data, setData] = useState<ContentIF | null>(null);
    const [loading, setLoading] = useState(true);
    const [comments, setComments] = useState<CommentIF[]>([]);
    const [heart, setHeart] = useState<number>(0);
    const [view, setView] = useState<number>(0);
    const [showDelAlert, setShowDelAlert] = useState(false);

    const serverURL = process.env.REACT_APP_API_URL;
    const content_id = Number(id);

    const navigate = useNavigate();

    useEffect(() => {
    setLoading(true);

    axios.get(`${process.env.REACT_APP_API_URL}/contents/getContents`)
        .then(res => {
            const tgt = res.data.find((item:ContentIF) => item.id === content_id);
            if (tgt){
                setData(tgt);
                setComments(tgt.comments || []);
                setHeart(tgt.heart);
                setView(tgt.view+1);
            }
            setLoading(false);
        })
        .catch(err => {
            console.error(err);
            setLoading(false);
        });
    }, [content_id]);

    const updateHeart = async () => {
        setHeart(heart+1);

        try {
            await increaseHeart(content_id);
        } catch(err) {
            alert("CONTENT 하트수 갱신 에러 발생!");
            console.error(err);
        }
    };

    const handleDeleteContent = async () => {
        try {
            await deleteContent(content_id);
            navigate('/');
        } catch(err) {
            alert("CONTENT 삭제 에러 발생!");
            console.error(err);
        }
    };

    // post는 data.post가 바뀔 때만 재계산하게끔!
    const PostBody = useMemo(() => {
        if(!data)
            return <Box>포스트를 찾을 수 없습니다...</Box>;

        return (
            <PostContainer>
                <ReactMarkdown urlTransform={(uri) => uri.startsWith('/database')
                            ? `${process.env.REACT_APP_API_URL}${uri}` : uri}
                            remarkPlugins={[remarkGfm]}
                            components={{
                                code(props) {
                                    const { className, style, children, ...rest } = props;
                                    const match = /language-(\w+)/.exec(className || '');
                                    return match ? (
                                        <SyntaxHighlighter
                                        style={dracula}
                                        language={match[1]}
                                        PreTag="div"
                                    >
                                        {String(children).replace(/\n$/, '')}
                                        </SyntaxHighlighter>) : (
                                        <code className={className} style={style} {...rest}>
                                            {children}
                                        </code>
                                    );
                                },
                            img: ({node, ...props}) => <img style={{maxWidth: '80%', borderRadius: "7px", display: 'block', marginLeft: 'auto', marginRight: 'auto'}}{...props} alt=""/>
                            }}>{data.post}
                </ReactMarkdown>
            </PostContainer>
        );
    }, [data?.post, serverURL]);

    return (<BG onClick={(e) => e.stopPropagation()}>
    <Header title="Lamiro" />
    <Body>
        { data ? (
             <>
            <TitleContainer>
                <Title>{data?.title || "Blog"}</Title>
                { isLoggedIn() ?
                <IconContainer style={{marginLeft: "auto", display: "flex", gap: "0.5rem"}}>
                    <ModifyIconContainer onClick={() => navigate(`/modify/${content_id}`)}>
                        <ModifyIcon />
                    </ModifyIconContainer>
                    <TrashIconContainer onClick={() => setShowDelAlert(true)}>
                        <TrashIcon />
                    </TrashIconContainer>
                </IconContainer> : <></>}
            </TitleContainer>
            <InfoContainer>
                <InfoSubContainer>
                    <DateTag>
                        <CalenderIconContainer>
                            <CalendarIcon />
                        </CalenderIconContainer>
                        {data.createdAt}
                    </DateTag>
                    <ViewIconContainer>
                        <ViewIcon />
                        {data.view}
                    </ViewIconContainer>
                    <HeartIconContainer onClick={updateHeart}>
                        <HeartIcon />
                        {heart}
                    </HeartIconContainer>
                </InfoSubContainer>
                {data.tags.length === 0
                    ? <InfoSubContainer>
                        <InfoSubTitle>Tags</InfoSubTitle>
                        <TagContainer>
                            <Tag $isNone={true}>
                            <TagIconContainer>
                                <TagIcon />
                                {"None"}
                            </TagIconContainer>
                        </Tag>
                        </TagContainer>
                    </InfoSubContainer>
                    : <InfoSubContainer>
                    <TagContainer>
                        {data.tags.map(tag => (
                            <Tag key={tag.id}>
                                <TagIconContainer>
                                    <TagIcon />
                                    {tag.name}
                                </TagIconContainer>
                            </Tag>
                        ))}
                    </TagContainer>
                </InfoSubContainer>
                }
            </InfoContainer>
            {PostBody}
            <CommentContainer>
                <Comments comments={comments} onComments={setComments} content_id={content_id}/>
            </CommentContainer>
            <Copyright />
            </>
        ) : loading ? (
            <WhileUnusual>Loading...</WhileUnusual>
        ) : (
            <WhileUnusual>존재하지 않는 게시물입니다.</WhileUnusual>
        )}
        {showDelAlert && (
        <AlertContainer>
            <DelContentAlert>
            <AlertIcon>⚠</AlertIcon>

            <AlertTitle>게시물 삭제</AlertTitle>

            <AlertDesc>
                <strong>"{data?.title || "게시물"}"</strong>을(를)<br />
                정말 삭제하시겠습니까?<br />
                삭제 후에는 복구할 수 없습니다.
            </AlertDesc>

            <BtnContainer>
                <CancelBtn onClick={() => setShowDelAlert(false)}>
                취소
                </CancelBtn>
                <DeleteBtn onClick={handleDeleteContent}>
                삭제
                </DeleteBtn>
            </BtnContainer>
            </DelContentAlert>
        </AlertContainer>
        )}
    </Body>
    </BG>);
}

export default Content;
